import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {RootState} from '@/store';
import {omitKey} from '@Utils/record.util';
import {loadStockFavoriteKeys} from '@Panels/stock-panel/stock-favorites-thunk';
import type {StockFavoriteKey, StockFavoritesState} from '@Panels/stock-panel/stock-favorites.types';
import {StockFavoritesLoadStatus} from '@Panels/stock-panel/stock-favorites.types';

/** The heart map's key for an item: the same pair the backend stores, joined so a plain object can index it. */
const getStockFavoriteMapKey = (key: StockFavoriteKey): string => {
  return `${key.itemSource}:${key.itemId}`;
};

const initialState: StockFavoritesState = {
  loadStatus: StockFavoritesLoadStatus.IDLE,
  favoriteKeys: {},
  pendingKeys: {},
};

const stockFavoritesSlice = createSlice({
  name: 'stockFavorites',
  initialState,
  reducers: {
    markStockFavorite(state, action: PayloadAction<StockFavoriteKey>) {
      state.favoriteKeys[getStockFavoriteMapKey(action.payload)] = true;
    },
    unmarkStockFavorite(state, action: PayloadAction<StockFavoriteKey>) {
      state.favoriteKeys = omitKey(state.favoriteKeys, getStockFavoriteMapKey(action.payload));
    },
    markStockFavoriteTogglePending(state, action: PayloadAction<StockFavoriteKey>) {
      state.pendingKeys[getStockFavoriteMapKey(action.payload)] = true;
    },
    clearStockFavoriteTogglePending(state, action: PayloadAction<StockFavoriteKey>) {
      state.pendingKeys = omitKey(state.pendingKeys, getStockFavoriteMapKey(action.payload));
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadStockFavoriteKeys.pending, (state) => {
      state.loadStatus = StockFavoritesLoadStatus.LOADING;
    });
    builder.addCase(loadStockFavoriteKeys.fulfilled, (state, action) => {
      state.favoriteKeys = Object.fromEntries(action.payload.map((key): [string, true] => [getStockFavoriteMapKey(key), true]));
      state.loadStatus = StockFavoritesLoadStatus.LOADED;
    });
    builder.addCase(loadStockFavoriteKeys.rejected, (state) => {
      state.loadStatus = StockFavoritesLoadStatus.ERROR;
    });
  },
});

export const {markStockFavorite, unmarkStockFavorite, markStockFavoriteTogglePending, clearStockFavoriteTogglePending} = stockFavoritesSlice.actions;
export const stockFavoritesReducer = stockFavoritesSlice.reducer;

export const isStockItemFavorited = (state: RootState, key: StockFavoriteKey): boolean => {
  return getStockFavoriteMapKey(key) in state.stockFavorites.favoriteKeys;
};

export const isStockFavoriteTogglePending = (state: RootState, key: StockFavoriteKey): boolean => {
  return getStockFavoriteMapKey(key) in state.stockFavorites.pendingKeys;
};

/** False for no items, so an empty selection never reads as all favorited. */
export const areAllStockItemsFavorited = (state: RootState, keys: StockFavoriteKey[]): boolean => {
  return keys.length > 0 && keys.every((key) => isStockItemFavorited(state, key));
};

/** How many of the items are not favorites yet: what a tap on the selection's empty heart adds. */
export const countStockItemsNotFavorited = (state: RootState, keys: StockFavoriteKey[]): number => {
  return keys.filter((key) => !isStockItemFavorited(state, key)).length;
};

export const isAnyStockFavoriteTogglePending = (state: RootState, keys: StockFavoriteKey[]): boolean => {
  return keys.some((key) => isStockFavoriteTogglePending(state, key));
};

export const getStockFavoritesLoadStatus = (state: RootState): StockFavoritesLoadStatus => {
  return state.stockFavorites.loadStatus;
};
